import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Modal, Tabs, Table, Tag, Button, Typography, Tooltip } from "antd"
import type { ColumnsType } from "antd/es/table"
import {
  FolderOutlined,
  ReadOutlined,
  RedoOutlined,
  FileTextOutlined,
  GlobalOutlined,
  LockOutlined,
  BookOutlined,
} from "@ant-design/icons"
import { setService } from "@/services/dictionary/setApi"
import { userService } from "@/services/user/userApi"
import { UserSetVocabulariesModal } from "@/views/notebook/user-set/components/UserSetVocabulariesModal"
import dayjs from "dayjs"

const { Text } = Typography

interface UserSetsModalProps {
  open: boolean
  user: any | null
  onClose: () => void
}

export const UserSetsModal: React.FC<UserSetsModalProps> = ({ open, user, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>("user_sets")
  const [userSetsPage, setUserSetsPage] = useState<number>(1)
  const [learnedSetsPage, setLearnedSetsPage] = useState<number>(1)
  const [selectedSet, setSelectedSet] = useState<any | null>(null)

  // 1. User Created Sets Query
  const {
    data: userSetsResult,
    isFetching: isFetchingUserSets,
    refetch: refetchUserSets,
  } = useQuery({
    queryKey: ["user_created_sets", user?.id, userSetsPage],
    queryFn: () => setService.list({ user_id: user?.id, type: "user", page: userSetsPage, limit: 10 }),
    enabled: open && !!user?.id && activeTab === "user_sets",
  })

  // 2. Learned Jupi Sets Query
  const {
    data: learnedSetsResult,
    isFetching: isFetchingLearnedSets,
    refetch: refetchLearnedSets,
  } = useQuery({
    queryKey: ["user_learned_sets", user?.id, learnedSetsPage],
    queryFn: () => userService.getLearnedSets(user?.id, { page: learnedSetsPage, limit: 10 }),
    enabled: open && !!user?.id && activeTab === "learned_sets",
  })

  // User sets columns
  const userSetsColumns: ColumnsType<any> = [
    { title: "ID", dataIndex: "id", width: 60, align: "center" },
    {
      title: "Tên bộ từ",
      key: "set_title",
      render: (_, record) => (
        <div
          className="flex cursor-pointer flex-col hover:opacity-80"
          onClick={() => setSelectedSet({ ...record, user })}
        >
          <div className="flex items-center gap-1.5 font-medium text-gray-800">
            <FileTextOutlined className="text-emerald-500" />
            <span className="font-bold text-primary hover:underline">{record.title}</span>
          </div>
          {record.full_title && record.full_title !== record.title && (
            <Text type="secondary" className="text-xs italic">
              {record.full_title}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      ellipsis: true,
      render: (val) => val || <Text type="secondary">—</Text>,
    },
    {
      title: "Quyền riêng tư",
      dataIndex: "visibility",
      width: 120,
      align: "center",
      render: (val) =>
        val === "public" ? (
          <Tag color="green" icon={<GlobalOutlined />}>
            Công khai
          </Tag>
        ) : (
          <Tag color="default" icon={<LockOutlined />}>
            Riêng tư
          </Tag>
        ),
    },
    {
      title: "Số từ vựng",
      dataIndex: "vocabulary_count",
      width: 110,
      align: "center",
      render: (count, record) => (
        <Tooltip title="Bấm để xem danh sách từ vựng trong bộ này">
          <Button type="link" size="small" icon={<BookOutlined />} onClick={() => setSelectedSet({ ...record, user })}>
            {count || 0} từ
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      width: 130,
      align: "center",
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Thao tác",
      key: "action",
      width: 80,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="Xem danh sách từ vựng">
          <Button
            type="text"
            size="small"
            icon={<BookOutlined className="text-base text-blue-500 hover:text-blue-600" />}
            onClick={() => setSelectedSet({ ...record, user })}
          />
        </Tooltip>
      ),
    },
  ]

  // Learned Jupi sets columns
  const learnedSetsColumns: ColumnsType<any> = [
    {
      title: "ID",
      key: "set_id",
      width: 70,
      align: "center",
      render: (_, record) => <span>{record.set?.id || record.set_id}</span>,
    },
    {
      title: "Bộ từ Jupi hệ thống",
      key: "set_title",
      render: (_, record) => {
        const setInfo = record.set || record
        const displayTitle = setInfo.full_title || setInfo.title || "—"
        return (
          <div className="flex items-center gap-1.5 font-medium text-gray-800">
            <ReadOutlined />
            <span>{displayTitle}</span>
          </div>
        )
      },
    },
    {
      title: "Danh mục",
      key: "category",
      width: 160,
      render: (_, record) => {
        const categoryName = record.set?.category?.name || record.category?.name
        return categoryName ? <Tag color="geekblue">{categoryName}</Tag> : <Text type="secondary">—</Text>
      },
    },
    {
      title: "Số từ",
      key: "vocabulary_count",
      width: 90,
      align: "center",
      render: (_, record) => {
        const count = record.set?.vocabulary_count ?? record.vocabulary_count
        return count !== undefined ? <Tag color="blue">{count} từ</Tag> : <Text type="secondary">—</Text>
      },
    },
    {
      title: "Lần ôn tập gần nhất",
      dataIndex: "last_reviewed_at",
      width: 160,
      align: "center",
      render: (val, record) => {
        const time = val || record.last_learned_at || record.updated_at
        return time ? dayjs(time).format("DD/MM/YYYY HH:mm") : "-"
      },
    },
  ]

  return (
    <>
      <Modal
        title={
          <div className="flex items-center justify-between pr-6">
            <div className="flex items-center gap-2">
              <span>Dữ liệu Bộ từ vựng:</span>
              <span className="font-semibold text-primary">
                {user?.name || user?.username || user?.email || "Người dùng"}
              </span>
              <Tag color="blue">#{user?.id}</Tag>
            </div>
            <Button
              size="small"
              icon={<RedoOutlined />}
              onClick={() => {
                if (activeTab === "user_sets") refetchUserSets()
                else refetchLearnedSets()
              }}
              loading={isFetchingUserSets || isFetchingLearnedSets}
            >
              Làm mới
            </Button>
          </div>
        }
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="close" type="primary" onClick={onClose}>
            Đóng
          </Button>,
        ]}
        width={950}
        destroyOnClose
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "user_sets",
              label: (
                <span>
                  <FolderOutlined className="mr-1" />
                  Bộ từ cá nhân ({userSetsResult?.paginate?.total || 0})
                </span>
              ),
              children: (
                <Table
                  rowKey="id"
                  columns={userSetsColumns}
                  dataSource={userSetsResult?.data || []}
                  loading={isFetchingUserSets}
                  pagination={{
                    current: userSetsPage,
                    pageSize: 10,
                    total: userSetsResult?.paginate?.total || 0,
                    onChange: (p) => setUserSetsPage(p),
                    size: "small",
                  }}
                  size="small"
                  scroll={{ x: 800 }}
                />
              ),
            },
            {
              key: "learned_sets",
              label: (
                <span>
                  <ReadOutlined className="mr-1" />
                  Bộ Jupi đã học ({learnedSetsResult?.paginate?.total || 0})
                </span>
              ),
              children: (
                <Table
                  rowKey="id"
                  columns={learnedSetsColumns}
                  dataSource={learnedSetsResult?.data || []}
                  loading={isFetchingLearnedSets}
                  pagination={{
                    current: learnedSetsPage,
                    pageSize: 10,
                    total: learnedSetsResult?.paginate?.total || 0,
                    onChange: (p) => setLearnedSetsPage(p),
                    size: "small",
                  }}
                  size="small"
                  scroll={{ x: 800 }}
                />
              ),
            },
          ]}
        />
      </Modal>

      {/* Modal chi tiết danh sách từ vựng trong bộ từ */}
      <UserSetVocabulariesModal open={!!selectedSet} record={selectedSet} onClose={() => setSelectedSet(null)} />
    </>
  )
}

export default UserSetsModal
